import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertCircle,
  Check,
  CheckCircle,
  Info,
  MapPin,
  Package,
  Star,
  Truck,
  X,
  XCircle,
  AlertTriangle,
  Navigation,
} from "lucide-react";
import logoFull from "@/assets/logo-full.svg";
import logo from "@/assets/logo.svg";

/* -------------------------------------------------------------------------- */
/*  Tokens                                                                    */
/* -------------------------------------------------------------------------- */

const PALETTE = {
  primary: [
    { name: "Primary 500", hex: "#FFBF07", text: "text-[#2D3748]" },
    { name: "Primary 600", hex: "#E5AB06", text: "text-[#2D3748]" },
    { name: "Primary 700", hex: "#CC9805", text: "text-white" },
  ],
  secondary: [
    { name: "Secondary 500", hex: "#37699E", text: "text-white" },
    { name: "Secondary 600", hex: "#2E5782", text: "text-white" },
    { name: "Secondary 700", hex: "#254566", text: "text-white" },
  ],
  accent: [
    { name: "Accent 500", hex: "#0880AE", text: "text-white" },
    { name: "Accent 600", hex: "#076A8E", text: "text-white" },
    { name: "Accent 700", hex: "#05536E", text: "text-white" },
  ],
  semantic: [
    { name: "Success", hex: "#14A38B", text: "text-white" },
    { name: "Warning", hex: "#F2AC57", text: "text-white" },
    { name: "Error", hex: "#FF7171", text: "text-white" },
  ],
  neutral: [
    { name: "Neutral 50", hex: "#F8F9FA", text: "text-[#1A202C]" },
    { name: "Neutral 100", hex: "#EBF4F8", text: "text-[#1A202C]" },
    { name: "Neutral 200", hex: "#DBE2EA", text: "text-[#1A202C]" },
    { name: "Neutral 300", hex: "#87ADD4", text: "text-white" },
    { name: "Neutral 500", hex: "#5A6B7A", text: "text-white" },
    { name: "Neutral 700", hex: "#2D3748", text: "text-white" },
    { name: "Neutral 900", hex: "#1A202C", text: "text-white" },
  ],
};

const SPACING = [
  { token: "p-2", px: "8px" },
  { token: "p-3", px: "12px" },
  { token: "p-4", px: "16px" },
  { token: "p-6", px: "24px" },
  { token: "p-8", px: "32px" },
  { token: "p-12", px: "48px" },
  { token: "p-16", px: "64px" },
];

const RADII = [
  { token: "rounded-md", px: "6px" },
  { token: "rounded-lg", px: "8px" },
  { token: "rounded-xl", px: "12px" },
  { token: "rounded-2xl", px: "16px" },
  { token: "rounded-3xl", px: "24px" },
  { token: "rounded-full", px: "9999px" },
];

const SHADOWS = [
  { token: "shadow-sm", usage: "Inputs focus, hover légers" },
  { token: "shadow-md", usage: "Cards au repos, dropdowns" },
  { token: "shadow-lg", usage: "Modals, floating buttons" },
  { token: "shadow-xl", usage: "Notifications, tooltips" },
];

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

const Section = ({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <section id={id} className="scroll-mt-24 space-y-6">
    <div>
      <h2
        className="text-3xl font-semibold text-[#37699E]"
        style={{ fontFamily: "Lexend Deca, sans-serif" }}
      >
        {title}
      </h2>
      {description && <p className="mt-2 text-sm text-[#5A6B7A]">{description}</p>}
    </div>
    <div className="rounded-2xl border border-[#DBE2EA] bg-white p-6 md:p-8 shadow-sm">
      {children}
    </div>
  </section>
);

const SubTitle = ({ children }: { children: React.ReactNode }) => (
  <h3
    className="text-lg font-medium text-[#2D3748] mb-4"
    style={{ fontFamily: "Lexend Deca, sans-serif" }}
  >
    {children}
  </h3>
);

const Swatch = ({
  name,
  hex,
  text,
}: {
  name: string;
  hex: string;
  text: string;
}) => (
  <div className="overflow-hidden rounded-xl border border-[#DBE2EA] shadow-sm">
    <div
      className={`flex h-24 items-end p-3 ${text}`}
      style={{ backgroundColor: hex }}
    >
      <span className="text-xs font-medium">{hex}</span>
    </div>
    <div className="bg-white p-3">
      <p className="text-sm font-medium text-[#2D3748]">{name}</p>
    </div>
  </div>
);

const NAV = [
  { id: "logo", label: "Logo" },
  { id: "colors", label: "Couleurs" },
  { id: "typography", label: "Typographie" },
  { id: "spacing", label: "Spacing" },
  { id: "radii", label: "Border radius" },
  { id: "shadows", label: "Ombres" },
  { id: "buttons", label: "Buttons" },
  { id: "forms", label: "Forms" },
  { id: "cards", label: "Cards" },
  { id: "badges", label: "Badges" },
  { id: "alerts", label: "Alerts" },
  { id: "progress", label: "Progress & loaders" },
  { id: "avatars", label: "Avatars & ratings" },
  { id: "navigation", label: "Navigation" },
  { id: "dialog", label: "Dialog" },
  { id: "tabs", label: "Tabs" },
  { id: "icons", label: "Icons" },
];

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

export default function DesignSystem() {
  const [progress] = useState(64);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="border-b border-[#DBE2EA] bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-8">
          <p
            className="text-xs font-medium uppercase tracking-wide text-[#0880AE]"
            style={{ fontFamily: "Funnel Sans, sans-serif" }}
          >
            VELOX
          </p>
          <h1
            className="mt-2 text-4xl font-semibold text-[#37699E] md:text-5xl"
            style={{ fontFamily: "Lexend Deca, sans-serif" }}
          >
            Design System
          </h1>
          <p className="mt-3 max-w-2xl text-base text-[#5A6B7A]">
            Référence centrale des couleurs, typographies, espacements et composants utilisés
            dans toute l'application VELOX.
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[220px,1fr]">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-6 lg:self-start">
            <nav className="rounded-2xl border border-[#DBE2EA] bg-white p-3 shadow-sm">
              <ul className="space-y-1">
                {NAV.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="block rounded-lg px-3 py-2 text-sm text-[#2D3748] transition-colors hover:bg-[#EBF4F8] hover:text-[#0880AE]"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Content */}
          <main className="space-y-12">
            {/* LOGO */}
            <Section id="logo" title="Logo" description="Variantes officielles du logo Velox.">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-[#DBE2EA] bg-white p-8">
                  <img src={logoFull} alt="Velox" className="h-10" />
                  <p className="text-xs text-[#5A6B7A]">logo-full.svg · navbar, landing</p>
                </div>
                <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-[#DBE2EA] bg-[#1A202C] p-8">
                  <img src={logo} alt="Velox" className="h-10" />
                  <p className="text-xs text-[#87ADD4]">logo.svg · icon, favicon</p>
                </div>
              </div>
            </Section>

            {/* COLORS */}
            <Section id="colors" title="Couleurs" description="Palette principale, sémantique et neutres.">
              <SubTitle>Primary · Yellow / Amber</SubTitle>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 mb-8">
                {PALETTE.primary.map((c) => <Swatch key={c.hex} {...c} />)}
              </div>
              <SubTitle>Secondary · Dark Blue</SubTitle>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 mb-8">
                {PALETTE.secondary.map((c) => <Swatch key={c.hex} {...c} />)}
              </div>
              <SubTitle>Accent · Teal</SubTitle>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 mb-8">
                {PALETTE.accent.map((c) => <Swatch key={c.hex} {...c} />)}
              </div>
              <SubTitle>Semantic</SubTitle>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 mb-8">
                {PALETTE.semantic.map((c) => <Swatch key={c.hex} {...c} />)}
              </div>
              <SubTitle>Neutral</SubTitle>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {PALETTE.neutral.map((c) => <Swatch key={c.hex} {...c} />)}
              </div>
            </Section>

            {/* TYPOGRAPHY */}
            <Section id="typography" title="Typographie" description="Lexend Deca pour les titres, Funnel Sans pour le corps.">
              <div className="space-y-6">
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[#5A6B7A]">Heading X · Hero · text-5xl</p>
                  <p className="text-5xl font-semibold text-[#37699E]" style={{ fontFamily: "Lexend Deca" }}>
                    Fast & Reliable Logistics
                  </p>
                </div>
                <Separator />
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[#5A6B7A]">Heading X2 · text-4xl</p>
                  <p className="text-4xl font-semibold text-[#37699E]" style={{ fontFamily: "Lexend Deca" }}>
                    Page title
                  </p>
                </div>
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[#5A6B7A]">Heading X3 · text-3xl</p>
                  <p className="text-3xl font-medium text-[#0880AE]" style={{ fontFamily: "Lexend Deca" }}>
                    Section title
                  </p>
                </div>
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[#5A6B7A]">Heading X4 · text-2xl</p>
                  <p className="text-2xl font-medium text-[#37699E]" style={{ fontFamily: "Lexend Deca" }}>
                    Card title
                  </p>
                </div>
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[#5A6B7A]">Heading X5 · text-xl</p>
                  <p className="text-xl font-medium text-[#2D3748]" style={{ fontFamily: "Lexend Deca" }}>
                    Form title
                  </p>
                </div>
                <Separator />
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[#5A6B7A]">Paragraph X1 · text-base</p>
                  <p className="text-base text-[#2D3748]" style={{ fontFamily: "Funnel Sans" }}>
                    Le transport routier européen demande fiabilité et précision. VELOX connecte les
                    expéditeurs et les transporteurs autour d'une plateforme unifiée.
                  </p>
                </div>
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[#5A6B7A]">Paragraph X2 · text-sm</p>
                  <p className="text-sm text-[#5A6B7A]" style={{ fontFamily: "Funnel Sans" }}>
                    Texte secondaire, captions et métadonnées.
                  </p>
                </div>
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[#5A6B7A]">Label · text-xs uppercase</p>
                  <p className="text-xs font-medium uppercase tracking-wide text-[#5A6B7A]">
                    Labels & metadata
                  </p>
                </div>
              </div>
            </Section>

            {/* SPACING */}
            <Section id="spacing" title="Spacing" description="Échelle de base 4px. Utiliser uniquement les multiples présents.">
              <div className="space-y-3">
                {SPACING.map((s) => (
                  <div key={s.token} className="flex items-center gap-4">
                    <code className="w-20 text-sm text-[#2D3748]">{s.token}</code>
                    <div className="h-3 rounded bg-[#0880AE]" style={{ width: s.px }} />
                    <span className="text-xs text-[#5A6B7A]">{s.px}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* RADII */}
            <Section id="radii" title="Border radius">
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
                {RADII.map((r) => (
                  <div key={r.token} className="text-center">
                    <div
                      className={`mx-auto h-20 w-20 bg-[#0880AE] ${r.token}`}
                    />
                    <p className="mt-2 text-sm font-medium text-[#2D3748]">{r.token}</p>
                    <p className="text-xs text-[#5A6B7A]">{r.px}</p>
                  </div>
                ))}
              </div>
            </Section>

            {/* SHADOWS */}
            <Section id="shadows" title="Ombres">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                {SHADOWS.map((s) => (
                  <div key={s.token} className="text-center">
                    <div className={`mx-auto h-24 w-full rounded-xl bg-white ${s.token}`} />
                    <p className="mt-3 text-sm font-medium text-[#2D3748]">{s.token}</p>
                    <p className="text-xs text-[#5A6B7A]">{s.usage}</p>
                  </div>
                ))}
              </div>
            </Section>

            {/* BUTTONS */}
            <Section id="buttons" title="Buttons">
              <SubTitle>Primary (Yellow CTA)</SubTitle>
              <div className="mb-8 flex flex-wrap items-center gap-3">
                <button className="rounded-lg bg-[#FFBF07] px-6 py-3 font-semibold text-[#2D3748] shadow-sm transition-all duration-200 hover:bg-[#E5AB06] hover:shadow-md active:bg-[#CC9805]">
                  Get Started
                </button>
                <button className="rounded-md bg-[#FFBF07] px-4 py-2 text-sm font-semibold text-[#2D3748] hover:bg-[#E5AB06]">
                  Small
                </button>
                <button className="rounded-xl bg-[#FFBF07] px-8 py-4 text-lg font-semibold text-[#2D3748] hover:bg-[#E5AB06]">
                  Large
                </button>
                <button disabled className="rounded-lg bg-[#FFBF07] px-6 py-3 font-semibold text-[#2D3748] opacity-50">
                  Disabled
                </button>
              </div>

              <SubTitle>Secondary (Blue Outline)</SubTitle>
              <div className="mb-8 flex flex-wrap items-center gap-3">
                <button className="rounded-lg border-2 border-[#37699E] bg-white px-6 py-3 font-semibold text-[#37699E] transition-colors hover:bg-[#EBF4F8]">
                  Cancel
                </button>
              </div>

              <SubTitle>Tertiary (Blue Solid)</SubTitle>
              <div className="mb-8 flex flex-wrap items-center gap-3">
                <button className="rounded-lg bg-[#37699E] px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-[#2E5782] hover:shadow-md">
                  Confirm
                </button>
              </div>

              <SubTitle>Ghost</SubTitle>
              <div className="mb-8 flex flex-wrap items-center gap-3">
                <button className="rounded-md px-4 py-2 font-medium text-[#0880AE] transition-colors hover:bg-[#EBF4F8]">
                  Forgot password?
                </button>
              </div>

              <SubTitle>shadcn / ui Button (variants existants)</SubTitle>
              <div className="flex flex-wrap items-center gap-3">
                <Button>Default</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="destructive">Destructive</Button>
                <Button variant="link">Link</Button>
              </div>
            </Section>

            {/* FORMS */}
            <Section id="forms" title="Forms">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="ds-input">Text input</Label>
                  <Input id="ds-input" placeholder="Type something..." />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ds-select">Select</Label>
                  <Select>
                    <SelectTrigger id="ds-select">
                      <SelectValue placeholder="Choose a trailer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="van">Van</SelectItem>
                      <SelectItem value="flatbed">Flatbed</SelectItem>
                      <SelectItem value="refrigerated">Refrigerated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="ds-textarea">Textarea</Label>
                  <Textarea id="ds-textarea" placeholder="Special instructions..." rows={4} />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="ds-check" />
                  <Label htmlFor="ds-check" className="cursor-pointer">Checkbox</Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch id="ds-switch" />
                  <Label htmlFor="ds-switch" className="cursor-pointer">Toggle switch</Label>
                </div>
                <RadioGroup defaultValue="a" className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="a" id="r-a" />
                    <Label htmlFor="r-a">Option A</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="b" id="r-b" />
                    <Label htmlFor="r-b">Option B</Label>
                  </div>
                </RadioGroup>
                <div className="space-y-2">
                  <Label>Slider</Label>
                  <Slider defaultValue={[40]} max={100} step={1} />
                </div>
              </div>
            </Section>

            {/* CARDS */}
            <Section id="cards" title="Cards">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {/* Pricing card */}
                <div className="space-y-4 rounded-2xl border border-[#DBE2EA] bg-white p-6 shadow-md transition-all duration-300 hover:border-[#0880AE] hover:shadow-lg">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-semibold text-[#37699E]" style={{ fontFamily: "Lexend Deca" }}>
                      850 €
                    </span>
                    <span className="text-sm text-[#5A6B7A]">Incl. VAT</span>
                  </div>
                  <h3 className="text-xl font-medium text-[#37699E]" style={{ fontFamily: "Lexend Deca" }}>
                    For the 25 November
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <Check className="mt-1 text-[#14A38B]" size={16} />
                      <span className="text-sm text-[#2D3748]">Between 8:00 - 10:00</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-1 text-[#0880AE]" size={16} />
                      <span className="text-sm text-[#2D3748]">Paris (FR) · Brussels (BE)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Package className="mt-1 text-[#5A6B7A]" size={16} />
                      <span className="text-sm text-[#2D3748]">Europe Shipper</span>
                    </div>
                  </div>
                  <button className="w-full rounded-lg bg-[#FFBF07] py-3 font-semibold text-[#2D3748] transition-colors hover:bg-[#E5AB06]">
                    Add Service
                  </button>
                </div>

                {/* Map card */}
                <div className="overflow-hidden rounded-2xl border border-[#DBE2EA] bg-white shadow-md">
                  <div className="flex h-40 items-center justify-center bg-[#EBF4F8]">
                    <Truck className="text-[#0880AE]" size={48} />
                  </div>
                  <div className="space-y-4 p-6">
                    <div className="flex items-center gap-2">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFBF07]">
                        <span className="font-bold text-white">V</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[#37699E]" style={{ fontFamily: "Lexend Deca" }}>
                          Delivery 11 Nov
                        </h3>
                        <p className="text-sm text-[#5A6B7A]">Order: #12345</p>
                      </div>
                    </div>
                    <button className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#FFBF07] py-3 font-semibold text-[#2D3748] hover:bg-[#E5AB06]">
                      <Navigation size={18} /> Begin delivery
                    </button>
                  </div>
                </div>

                {/* shadcn Card */}
                <Card>
                  <CardHeader>
                    <CardTitle>shadcn Card</CardTitle>
                    <CardDescription>Composant standard du design system.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#5A6B7A]">
                      Utilise les tokens semantic pour rester cohérent avec le thème global.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </Section>

            {/* BADGES */}
            <Section id="badges" title="Badges & Pills">
              <div className="mb-6 flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded px-2 py-1 text-xs font-medium text-white" style={{ backgroundColor: "#37699E" }}>Secondary</span>
                <span className="inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium text-white" style={{ backgroundColor: "#0880AE" }}>Accent</span>
                <span className="inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium text-[#2D3748]" style={{ backgroundColor: "#FFBF07" }}>Primary</span>
                <span className="inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium text-white" style={{ backgroundColor: "#14A38B" }}>Success</span>
                <span className="inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium text-white" style={{ backgroundColor: "#F2AC57" }}>Warning</span>
                <span className="inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium text-white" style={{ backgroundColor: "#FF7171" }}>Error</span>
              </div>
              <SubTitle>shadcn Badge</SubTitle>
              <div className="flex flex-wrap gap-2">
                <Badge>Default</Badge>
                <Badge variant="secondary">Secondary</Badge>
                <Badge variant="outline">Outline</Badge>
                <Badge variant="destructive">Destructive</Badge>
              </div>
            </Section>

            {/* ALERTS */}
            <Section id="alerts" title="Alerts">
              <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-lg border border-[#0880AE]/20 bg-[#0880AE]/10 p-4">
                  <Info className="mt-0.5 flex-shrink-0 text-[#0880AE]" size={20} />
                  <p className="flex-1 text-sm text-[#2D3748]">Alert info — message d'information pour l'utilisateur.</p>
                  <button className="text-[#0880AE] hover:text-[#076A8E]"><X size={16} /></button>
                </div>
                <div className="flex items-start gap-3 rounded-lg p-4 text-white" style={{ backgroundColor: "#0880AE" }}>
                  <AlertCircle className="mt-0.5 flex-shrink-0" size={20} />
                  <p className="flex-1 text-sm">Primary alert — action importante à connaître.</p>
                  <button><X size={16} /></button>
                </div>
                <div className="flex items-start gap-3 rounded-lg p-4 text-white" style={{ backgroundColor: "#F2AC57" }}>
                  <AlertTriangle className="mt-0.5 flex-shrink-0" size={20} />
                  <p className="flex-1 text-sm">Warning — attention requise.</p>
                  <button><X size={16} /></button>
                </div>
                <div className="flex items-start gap-3 rounded-lg p-4 text-white" style={{ backgroundColor: "#14A38B" }}>
                  <CheckCircle className="mt-0.5 flex-shrink-0" size={20} />
                  <p className="flex-1 text-sm">Success — action effectuée avec succès.</p>
                  <button><X size={16} /></button>
                </div>
                <div className="flex items-start gap-3 rounded-lg p-4 text-white" style={{ backgroundColor: "#FF7171" }}>
                  <XCircle className="mt-0.5 flex-shrink-0" size={20} />
                  <p className="flex-1 text-sm">Error — quelque chose s'est mal passé.</p>
                  <button><X size={16} /></button>
                </div>
                <SubTitle>shadcn Alert</SubTitle>
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>Heads up</AlertTitle>
                  <AlertDescription>Default shadcn alert component.</AlertDescription>
                </Alert>
              </div>
            </Section>

            {/* PROGRESS & LOADERS */}
            <Section id="progress" title="Progress & loaders">
              <div className="space-y-6">
                <div>
                  <Label className="mb-2 inline-block">Progress (shadcn) — {progress}%</Label>
                  <Progress value={progress} />
                </div>
                <div className="space-y-3">
                  {[
                    { color: "#0880AE", val: 60 },
                    { color: "#F2AC57", val: 40 },
                    { color: "#14A38B", val: 80 },
                    { color: "#FF7171", val: 30 },
                  ].map((p) => (
                    <div key={p.color} className="h-2 w-full overflow-hidden rounded-full bg-[#DBE2EA]">
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{ width: `${p.val}%`, backgroundColor: p.color }}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-6">
                  {["#0880AE", "#F2AC57", "#14A38B", "#FF7171"].map((c) => (
                    <div
                      key={c}
                      className="h-8 w-8 animate-spin rounded-full border-4 border-[#DBE2EA]"
                      style={{ borderTopColor: c }}
                    />
                  ))}
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-20 w-full" />
                </div>
              </div>
            </Section>

            {/* AVATARS & RATINGS */}
            <Section id="avatars" title="Avatars & ratings">
              <div className="mb-6 flex items-end gap-4">
                <Avatar className="h-8 w-8"><AvatarFallback>SM</AvatarFallback></Avatar>
                <Avatar className="h-10 w-10"><AvatarFallback>MD</AvatarFallback></Avatar>
                <Avatar className="h-12 w-12"><AvatarFallback>LG</AvatarFallback></Avatar>
                <Avatar className="h-16 w-16"><AvatarImage src="https://i.pravatar.cc/100" /><AvatarFallback>XL</AvatarFallback></Avatar>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4].map((i) => (
                  <Star key={i} className="fill-[#FFBF07] text-[#FFBF07]" size={18} />
                ))}
                <Star className="text-[#DBE2EA]" size={18} />
                <span className="ml-2 text-sm font-medium text-[#5A6B7A]">4.0</span>
              </div>
            </Section>

            {/* NAVIGATION */}
            <Section id="navigation" title="Navigation">
              <SubTitle>Horizontal navbar (desktop)</SubTitle>
              <nav className="rounded-xl bg-white shadow-sm">
                <div className="flex h-16 items-center justify-between px-6">
                  <img src={logoFull} alt="Velox" className="h-8" />
                  <div className="hidden items-center gap-8 md:flex">
                    {["Dashboard", "Orders", "Marketplace", "Settings"].map((i) => (
                      <a key={i} href="#" className="font-medium text-[#2D3748] transition-colors hover:text-[#0880AE]">{i}</a>
                    ))}
                  </div>
                  <button className="rounded-lg bg-[#FFBF07] px-6 py-2 font-semibold text-[#2D3748] hover:bg-[#E5AB06]">
                    Get Started
                  </button>
                </div>
              </nav>
            </Section>

            {/* DIALOG */}
            <Section id="dialog" title="Dialog / Modal">
              <Dialog>
                <DialogTrigger asChild>
                  <Button>Open dialog</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Confirmer l'action</DialogTitle>
                    <DialogDescription>
                      Cette action est irréversible. Veux-tu continuer ?
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline">Annuler</Button>
                    <Button>Confirmer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </Section>

            {/* TABS */}
            <Section id="tabs" title="Tabs">
              <Tabs defaultValue="overview">
                <TabsList>
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>
                <TabsContent value="overview" className="pt-4 text-sm text-[#2D3748]">
                  Vue d'ensemble de la mission.
                </TabsContent>
                <TabsContent value="details" className="pt-4 text-sm text-[#2D3748]">
                  Détails complets et historique.
                </TabsContent>
                <TabsContent value="settings" className="pt-4 text-sm text-[#2D3748]">
                  Réglages avancés.
                </TabsContent>
              </Tabs>
            </Section>

            {/* ICONS */}
            <Section id="icons" title="Icons (lucide-react)" description="Tailles standards : 16, 20, 24.">
              <div className="grid grid-cols-3 gap-4 md:grid-cols-6">
                {[
                  { Icon: MapPin, label: "MapPin" },
                  { Icon: Navigation, label: "Navigation" },
                  { Icon: Truck, label: "Truck" },
                  { Icon: Package, label: "Package" },
                  { Icon: Check, label: "Check" },
                  { Icon: X, label: "X" },
                  { Icon: AlertCircle, label: "AlertCircle" },
                  { Icon: Star, label: "Star" },
                  { Icon: Info, label: "Info" },
                  { Icon: CheckCircle, label: "CheckCircle" },
                  { Icon: XCircle, label: "XCircle" },
                  { Icon: AlertTriangle, label: "AlertTriangle" },
                ].map(({ Icon, label }) => (
                  <div key={label} className="flex flex-col items-center gap-2 rounded-lg border border-[#DBE2EA] p-4">
                    <Icon className="text-[#0880AE]" size={24} />
                    <span className="text-xs text-[#5A6B7A]">{label}</span>
                  </div>
                ))}
              </div>
            </Section>

            <footer className="pt-8 text-center text-xs text-[#5A6B7A]">
              VELOX Design System · synced with the project specs
            </footer>
          </main>
        </div>
      </div>
    </div>
  );
}
